import type { ExtractedTableData } from '@neo/blocks/data-table/types'

/**
 * Minimal shape of the pagination `meta` on the v1 list endpoints (Call, Recording, ...).
 *
 * `total_count` and `has_more` are BOTH optional on purpose: the provider (`rest-api`) is
 * migrating from `total_count` to `has_more` behind the per-account `use_limit_plus_one`
 * flag, so during rollout the console receives a mix of the old and new shapes and must
 * interpret either. `next`/`previous` are unchanged across both.
 */
export interface ListPaginationMeta {
  total_count?: number
  has_more?: boolean
  next?: string | null
  previous?: string | null
}

/**
 * Map a list response's `meta` to the RemoteTable pagination fields, tolerating both the
 * legacy (`total_count`) and new (`has_more`) pagination contracts so they co-exist during
 * the rollout window regardless of API-vs-console deploy order:
 *  - `totalCount`: the exact total when present (legacy) → footer shows "X–Y of N"; absent
 *    (new) → `undefined` → footer shows "X–Y of many".
 *  - `hasNext`: the explicit `has_more` flag when present (new); otherwise fall back to the
 *    `next` URL (legacy / any response that omits the flag).
 *  - `hasPrevious`: the `previous` URL (present in both shapes).
 */
export const extractPaginationFromMeta = (
  meta?: ListPaginationMeta,
): Pick<ExtractedTableData<unknown>, 'totalCount' | 'hasNext' | 'hasPrevious'> => ({
  totalCount: meta?.total_count ?? undefined,
  hasNext: meta?.has_more ?? !!meta?.next,
  hasPrevious: !!meta?.previous,
})

/**
 * Clamp a page-size param to `[1, cap]`, falling back to `cap` when it isn't a number.
 *
 * Several v1 list endpoints (Recording, Transcription) silently cap `limit` server-side:
 * asking for 100 returns `cap` rows with no error, which would leave the table believing
 * it requested a larger page than it received. Clamping before the request keeps the
 * requested and returned page sizes in agreement.
 */
export const clampPageSize = (value: unknown, cap: number): number => {
  const parsed = parseInt(String(value), 10)
  return Number.isNaN(parsed) ? cap : Math.min(Math.max(parsed, 1), cap)
}
